import { useEffect, useMemo, useState } from "react";
import { Alert, Card, CircularProgress, Grid, IconButton } from "@mui/material";

import {
  FaClipboardCheck,
  FaClipboardList,
  FaEdit,
  FaListAlt,
  FaPaperPlane,
  FaPercentage,
  FaPlus,
} from "react-icons/fa";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import Snackbar from "components/Snackbar";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import colors from "assets/theme/base/colors";
import borders from "assets/theme/base/borders";
import rgba from "assets/theme/functions/rgba";

import HotelSelector from "components/HotelSelector";
import DateRangeFilter from "components/DateRangePicker";
import MonthSelector from "components/MonthSelector";
import KpiCard from "components/KpiCard";

import SatisfactionTrendChart from "./components/SatisfactionTrendChart";
import GuestSatisfactionFormDialog from "./components/GuestSatisfactionFormDialog";
import MultipleChoiceAnalyticsCard from "./components/MultipleChoiceAnalyticsCard";
import TextFeedbackAnalyticsCard from "./components/TextFeedbackAnalyticsCard";
import TextFeedbackSentimentPanel from "./components/TextFeedbackSentimentPanel";

import {
  buildSatisfactionAnalyticsParams,
  formatSatisfactionDelta,
  getSatisfactionDeltaColor,
  mapChoiceAnalytics,
  mapRatingAnalytics,
  mapSatisfactionQuestions,
  mapTextAnalytics,
  mapTextFeedbackSummary,
} from "./utils/satisfactionHelpers";

import useCommon from "hooks/useCommon";
import dayjs from "dayjs";

import {
  createSatisfactionForm,
  getRecentSatisfactionTextAnswers,
  getSatisfactionCounters,
  getSatisfactionForm,
  getSatisfactionMultipleChoiceStats,
  getSatisfactionRatingStats,
  getSatisfactionTextSummary,
  resetCreateSatisfactionFormState,
  resetUpdateSatisfactionFormState,
  updateSatisfactionForm,
} from "store/slice/satisfactionSlice";
import usePermissions from "hooks/usePermissions";

const { black, dark, primary } = colors;
const { borderWidth } = borders;

function GuestSatisfactionHub() {
  const { DISPATCH, STORE } = useCommon();
  const { can } = usePermissions();

  const {
    satisfactionForm,

    counters,
    textSummary,
    ratingStats,
    multipleChoiceStats,
    recentTextAnswers,

    getFormLoading,
    getFormError,

    createFormLoading,
    createFormSuccess,
    createFormError,

    updateFormLoading,
    updateFormSuccess,
    updateFormError,

    countersLoading,
    countersError,

    textSummaryLoading,
    textSummaryError,

    ratingStatsLoading,
    ratingStatsError,

    multipleChoiceStatsLoading,
    multipleChoiceStatsError,

    recentTextAnswersLoading,
    recentTextAnswersError,
  } = STORE((state) => state.satisfaction);

  const hotelId = STORE((state) => state.ui.hotelId);

  const [selectedMonth, setSelectedMonth] = useState(dayjs().format("YYYY-MM"));
  const [dateRange, setDateRange] = useState(["", ""]);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    show: false,
    color: "success",
    title: "",
    content: "",
    icon: "check",
  });

  const showSnackbar = ({ color = "success", title = "", content = "", icon = "check" }) => {
    setSnackbar({
      show: true,
      color,
      title,
      content,
      icon,
    });
  };

  const closeSnackbar = () => {
    setSnackbar((current) => ({
      ...current,
      show: false,
    }));
  };

  const hasForm = Boolean(satisfactionForm?.formId);

  const formActionLoading = createFormLoading || updateFormLoading;

  const formActionError = createFormError || updateFormError;

  const analyticsLoading =
    countersLoading ||
    textSummaryLoading ||
    ratingStatsLoading ||
    multipleChoiceStatsLoading ||
    recentTextAnswersLoading;

  const analyticsError =
    countersError ||
    textSummaryError ||
    ratingStatsError ||
    multipleChoiceStatsError ||
    recentTextAnswersError;

  const analyticsParams = useMemo(
    () =>
      buildSatisfactionAnalyticsParams({
        hotelId,
        selectedMonth,
        dateRange,
      }),
    [hotelId, selectedMonth, dateRange]
  );

  const ratingAnalytics = useMemo(() => mapRatingAnalytics(ratingStats), [ratingStats]);

  const choiceAnalytics = useMemo(
    () => mapChoiceAnalytics(multipleChoiceStats),
    [multipleChoiceStats]
  );

  const textAnalytics = useMemo(
    () =>
      mapTextAnalytics({
        recentTextAnswers,
        textSummary,
      }),
    [recentTextAnswers, textSummary]
  );

  const textFeedbackSummary = useMemo(() => mapTextFeedbackSummary(textSummary));

  const formKpiAnalytics = useMemo(
    () => [
      {
        id: "forms_sent",
        title: "Forms Sent",
        value: counters?.formSent?.value ?? 0,
        sub: formatSatisfactionDelta(counters?.formSent?.deltaPct),
        color: getSatisfactionDeltaColor(counters?.formSent?.deltaPct),
        icon: <FaPaperPlane size={16} />,
      },
      {
        id: "total_responses",
        title: "Total Responses",
        value: counters?.totalResponses?.value ?? 0,
        sub: formatSatisfactionDelta(counters?.totalResponses?.deltaPct),
        color: getSatisfactionDeltaColor(counters?.totalResponses?.deltaPct),
        icon: <FaClipboardCheck size={16} />,
      },
      {
        id: "response_rate",
        title: "Response Rate",
        value: `${counters?.responseRate?.value ?? 0}%`,
        sub: formatSatisfactionDelta(counters?.responseRate?.deltaPct),
        color: getSatisfactionDeltaColor(counters?.responseRate?.deltaPct),
        icon: <FaPercentage size={16} />,
      },
      {
        id: "completion_ratio",
        title: "Completion Ratio",
        value: `${counters?.completionRatio?.value ?? 0}%`,
        sub: formatSatisfactionDelta(counters?.completionRatio?.deltaPct),
        color: getSatisfactionDeltaColor(counters?.completionRatio?.deltaPct),
        icon: <FaListAlt size={16} />,
      },
    ],
    [counters]
  );

  const handleMonthChange = (value) => {
    const monthValue = value?.target?.value ?? value ?? "";

    setSelectedMonth(monthValue);
    setDateRange(["", ""]);
  };

  const handleOpenFormDialog = () => {
    if (!hotelId) {
      return;
    }

    DISPATCH(resetCreateSatisfactionFormState());
    DISPATCH(resetUpdateSatisfactionFormState());

    setFormDialogOpen(true);
  };

  const handleCloseFormDialog = () => {
    if (formActionLoading) {
      return;
    }

    DISPATCH(resetCreateSatisfactionFormState());
    DISPATCH(resetUpdateSatisfactionFormState());

    setFormDialogOpen(false);
  };

  const handleDateRangeChange = (value) => {
    setDateRange(value);
    setSelectedMonth("");
  };
  const handleSaveForm = async (payload) => {
    if (!hotelId || !payload || formActionLoading) {
      return;
    }

    const questions = mapSatisfactionQuestions({
      questions: payload.questions || [],
      hasForm,
    });

    const name = payload.name || payload.formName || "";
    const description = payload.description || "";

    try {
      if (hasForm) {
        const response = await DISPATCH(
          updateSatisfactionForm({
            formId: satisfactionForm.formId,
            payload: {
              lastKnownUpdatedAt: satisfactionForm.updatedAt,
              name,
              description,
              questions,
            },
          })
        ).unwrap();

        setFormDialogOpen(false);

        showSnackbar({
          color: "success",
          title: "Form Updated",
          content: response?.message || "Guest satisfaction form updated successfully.",
          icon: "check",
        });

        DISPATCH(resetUpdateSatisfactionFormState());

        return;
      }

      const response = await DISPATCH(
        createSatisfactionForm({
          hotelId,
          name,
          description,
          questions,
        })
      ).unwrap();

      setFormDialogOpen(false);

      showSnackbar({
        color: "success",
        title: "Form Created",
        content: response?.message || "Guest satisfaction form created successfully.",
        icon: "check",
      });

      DISPATCH(resetCreateSatisfactionFormState());
    } catch (error) {
      showSnackbar({
        color: "error",
        title: hasForm ? "Unable to Update Form" : "Unable to Create Form",
        content:
          typeof error === "string"
            ? error
            : error?.message ||
              `Failed to ${hasForm ? "update" : "create"} guest satisfaction form.`,
        icon: "warning",
      });
    }
  };
  useEffect(() => {
    if (!hasForm || !analyticsParams) {
      return;
    }

    DISPATCH(getSatisfactionCounters(analyticsParams));
    DISPATCH(getSatisfactionTextSummary(analyticsParams));
    DISPATCH(getSatisfactionRatingStats(analyticsParams));
    DISPATCH(getSatisfactionMultipleChoiceStats(analyticsParams));
    DISPATCH(getRecentSatisfactionTextAnswers(analyticsParams));
  }, [DISPATCH, hasForm, analyticsParams]);

  useEffect(() => {
    if (!hotelId) {
      return;
    }

    DISPATCH(
      getSatisfactionForm({
        hotelId,
      })
    );
  }, [DISPATCH, hotelId]);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <VuiBox py={0}>
        <Card sx={{ p: 2 }}>
          {/* Header */}
          <VuiBox
            mb={1.5}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
          >
            <VuiBox>
              <VuiTypography variant="lg" color="dark">
                Guest Satisfaction Metrics
              </VuiTypography>
            </VuiBox>

            {hasForm && can("editSatisfactionForm") && (
              <VuiButton
                color="primary"
                onClick={handleOpenFormDialog}
                startIcon={<FaEdit />}
                disabled={!hotelId || getFormLoading}
              >
                Edit Form
              </VuiButton>
            )}
          </VuiBox>

          {/* Analytics error */}
          {analyticsError && hasForm && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {analyticsError}
            </Alert>
          )}

          {/* Select hotel before loading form */}
          {!hotelId && (
            <VuiBox
              minHeight="420px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              borderRadius="20px"
              sx={sectionStyles}
            >
              <VuiBox
                maxWidth="520px"
                textAlign="center"
                display="flex"
                flexDirection="column"
                alignItems="center"
                px={2}
                py={5}
              >
                <VuiBox
                  width={64}
                  height={64}
                  borderRadius="18px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  mb={2}
                  sx={{
                    background: rgba(primary.main, 0.1),
                    border: `${borderWidth[1]} solid ${rgba(primary.main, 0.2)}`,
                    color: primary.main,
                  }}
                >
                  <FaClipboardList size={26} />
                </VuiBox>

                <VuiTypography variant="h5" color="dark" fontWeight="bold" mb={1}>
                  Select a Hotel
                </VuiTypography>

                <VuiTypography
                  variant="button"
                  color="dark"
                  fontWeight="regular"
                  sx={{
                    opacity: 0.72,
                    lineHeight: 1.7,
                    maxWidth: "440px",
                  }}
                >
                  Select a hotel to create or manage its guest satisfaction form and view the
                  satisfaction analytics.
                </VuiTypography>

                <VuiBox
                  mt={3}
                  sx={{
                    width: {
                      xs: "100%",
                      sm: "260px",
                    },
                  }}
                >
                  <HotelSelector />
                </VuiBox>
              </VuiBox>
            </VuiBox>
          )}

          {/* Form loading */}
          {hotelId && getFormLoading && (
            <VuiBox minHeight="350px" display="flex" alignItems="center" justifyContent="center">
              <CircularProgress size={36} />
            </VuiBox>
          )}

          {/* No form found */}
          {hotelId && !getFormLoading && !hasForm && (
            <VuiBox
              minHeight="420px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              borderRadius="20px"
              sx={sectionStyles}
            >
              <VuiBox
                maxWidth="520px"
                textAlign="center"
                display="flex"
                flexDirection="column"
                alignItems="center"
                px={2}
                py={5}
              >
                <VuiBox
                  width={64}
                  height={64}
                  borderRadius="18px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  mb={2}
                  sx={{
                    background: rgba(primary.main, 0.1),
                    border: `${borderWidth[1]} solid ${rgba(primary.main, 0.2)}`,
                    color: primary.main,
                  }}
                >
                  <FaClipboardList size={26} />
                </VuiBox>

                <VuiTypography variant="h5" color="dark" fontWeight="bold" mb={1}>
                  No Guest Satisfaction Form Yet
                </VuiTypography>

                <VuiTypography
                  variant="button"
                  color="dark"
                  fontWeight="regular"
                  sx={{
                    opacity: 0.72,
                    lineHeight: 1.7,
                    maxWidth: "440px",
                  }}
                >
                  Create a satisfaction form to start collecting guest feedback, tracking response
                  rates, and viewing survey analytics.
                </VuiTypography>

                <VuiBox
                  mt={3}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  gap={1.5}
                  flexWrap="wrap"
                >
                  <VuiBox
                    sx={{
                      minWidth: {
                        xs: "100%",
                        sm: "240px",
                      },
                    }}
                  >
                    <HotelSelector />
                  </VuiBox>

                  {can("createSatisfactionForm") && (
                    <VuiButton
                      color="primary"
                      startIcon={<FaPlus />}
                      onClick={handleOpenFormDialog}
                    >
                      Create Form
                    </VuiButton>
                  )}
                </VuiBox>
              </VuiBox>
            </VuiBox>
          )}

          {/* Form content */}
          {hotelId && !getFormLoading && hasForm && (
            <>
              {/* Form Summary + Filters */}
              <VuiBox mb={2} p={{ xs: 1.4, md: 1.5 }} borderRadius="20px" sx={sectionStyles}>
                <VuiBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems={{
                    xs: "stretch",
                    lg: "flex-start",
                  }}
                  gap={{ xs: 1.5, lg: 2.5 }}
                  flexDirection={{
                    xs: "column",
                    lg: "row",
                  }}
                >
                  {/* Form Info */}
                  <VuiBox flex={1} minWidth={0}>
                    <VuiTypography variant="h6" fontWeight="bold" color="dark">
                      {satisfactionForm?.name || "Guest Satisfaction Form"}
                    </VuiTypography>

                    <VuiTypography
                      display="block"
                      variant="caption"
                      color="dark"
                      fontWeight="regular"
                      sx={{
                        mt: 0.5,
                        maxWidth: "620px",
                        opacity: 0.75,
                        lineHeight: 1.6,
                      }}
                    >
                      {satisfactionForm?.description || "No description available."}
                    </VuiTypography>

                    <VuiBox
                      mt={0.75}
                      display="grid"
                      gridTemplateColumns={{
                        xs: "1fr 1fr",
                        sm: "repeat(3, auto)",
                      }}
                      gap={{ xs: 0.5, sm: 2 }}
                      alignItems="start"
                    >
                      <VuiBox>
                        <VuiTypography
                          variant="caption"
                          color="dark"
                          fontWeight="regular"
                          sx={{ opacity: 0.65 }}
                        >
                          Questions
                        </VuiTypography>

                        <VuiTypography
                          display="block"
                          variant="caption"
                          color="dark"
                          fontWeight="bold"
                        >
                          {satisfactionForm?.questions?.length || 0}
                        </VuiTypography>
                      </VuiBox>

                      {/* <VuiBox>
                        <VuiTypography
                          variant="caption"
                          color="dark"
                          fontWeight="regular"
                          sx={{ opacity: 0.65 }}
                        >
                          Schema Version
                        </VuiTypography>

                        <VuiTypography
                          display="block"
                          variant="caption"
                          color="dark"
                          fontWeight="bold"
                        >
                          {satisfactionForm?.schemaVersion || 1}
                        </VuiTypography>
                      </VuiBox> */}

                      <VuiBox>
                        <VuiTypography
                          variant="caption"
                          color="dark"
                          fontWeight="regular"
                          sx={{ opacity: 0.65 }}
                        >
                          Delivery Method
                        </VuiTypography>

                        <VuiTypography
                          display="block"
                          variant="caption"
                          color="dark"
                          fontWeight="bold"
                        >
                          SMS
                        </VuiTypography>
                      </VuiBox>

                      <VuiBox>
                        <VuiTypography
                          variant="caption"
                          color="dark"
                          fontWeight="regular"
                          sx={{ opacity: 0.65 }}
                        >
                          Last Updated
                        </VuiTypography>

                        <VuiTypography
                          display="block"
                          variant="caption"
                          color="dark"
                          fontWeight="bold"
                        >
                          {satisfactionForm?.updatedAt
                            ? new Date(satisfactionForm.updatedAt).toLocaleDateString()
                            : "N/A"}
                        </VuiTypography>
                      </VuiBox>
                    </VuiBox>
                  </VuiBox>

                  {/* Filters */}
                  <VuiBox
                    display="grid"
                    gridTemplateColumns={{
                      xs: "1fr",
                      sm: "1fr 1fr",
                      lg: "220px auto auto auto",
                    }}
                    alignItems="center"
                    gap={1}
                    sx={{
                      width: {
                        xs: "100%",
                        lg: "auto",
                      },

                      pt: {
                        xs: 1.4,
                        lg: 0,
                      },

                      borderTop: {
                        xs: `${borderWidth[1]} solid ${rgba(black.main, 0.08)}`,
                        lg: "none",
                      },
                    }}
                  >
                    <VuiBox
                      sx={{
                        minWidth: {
                          xs: "100%",
                          lg: "220px",
                        },
                      }}
                    >
                      <HotelSelector />
                    </VuiBox>

                    <MonthSelector
                      value={selectedMonth}
                      clearable={false}
                      onChange={(value) => {
                        if (!value) return;
                        setSelectedMonth(value);
                        setDateRange(["", ""]);
                      }}
                    />
                    {/* <DateRangeFilter
                      value={dateRange}
                      onChange={(value) => {
                        setDateRange(value);
                        setSelectedMonth("");
                      }}
                    /> */}

                    <DateRangeFilter value={dateRange} onChange={setDateRange} />

                    {can("editSatisfactionForm") && (
                      <IconButton
                        onClick={handleOpenFormDialog}
                        disabled={formActionLoading}
                        sx={{
                          p: 1,

                          width: {
                            xs: "100%",
                            lg: "auto",
                          },

                          minHeight: "38px",
                          color: dark.main,

                          background: rgba(black.main, 0.04),

                          border: `${borderWidth[1]} solid ${rgba(black.main, 0.12)}`,

                          borderRadius: "12px",

                          "&:hover": {
                            background: rgba(black.main, 0.08),
                          },
                        }}
                      >
                        <FaEdit size={14} />
                      </IconButton>
                    )}
                  </VuiBox>
                </VuiBox>
              </VuiBox>

              {/* Select date filter state */}
              {!analyticsParams && (
                <VuiBox display="flex" justifyContent="center" width="100%" mb={2}>
                  <Alert
                    severity="info"
                    variant="light"
                    sx={{
                      width: "fit-content",
                      textAlign: "center",
                    }}
                  >
                    Select a month or date range to load satisfaction analytics.
                  </Alert>
                </VuiBox>
              )}

              {/* Analytics loading */}
              {analyticsLoading && analyticsParams && (
                <VuiBox
                  minHeight="180px"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <CircularProgress size={34} />
                </VuiBox>
              )}

              {/* Analytics content */}
              {!analyticsLoading && analyticsParams && (
                <>
                  {/* KPI Cards */}
                  <Grid container spacing={3} mb={0}>
                    {formKpiAnalytics.map((item) => (
                      <Grid item xs={12} sm={6} lg={3} key={item.id}>
                        <KpiCard
                          title={item.title}
                          value={item.value}
                          sub={item.sub}
                          color={item.color}
                          icon={item.icon}
                        />
                      </Grid>
                    ))}
                  </Grid>

                  {/* Text Feedback Sentiment */}
                  {textFeedbackSummary && (
                    <Grid container spacing={2} mt={0} mb={2}>
                      <Grid item xs={12}>
                        <TextFeedbackSentimentPanel summary={textFeedbackSummary} />
                      </Grid>
                    </Grid>
                  )}

                  {/* Rating Trends */}
                  {ratingAnalytics.length > 0 && (
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <VuiBox p={2.5} borderRadius="20px" sx={sectionStyles}>
                          <VuiTypography variant="h6" fontWeight="bold" color="dark" mb={2}>
                            Satisfaction Trends
                          </VuiTypography>

                          <Grid container spacing={2}>
                            {ratingAnalytics.map((item) => (
                              <Grid item xs={12} key={item.questionId}>
                                <SatisfactionTrendChart item={item} />
                              </Grid>
                            ))}
                          </Grid>
                        </VuiBox>
                      </Grid>
                    </Grid>
                  )}

                  {/* Multiple Choice Analytics */}
                  {choiceAnalytics.length > 0 && (
                    <Grid container spacing={3} mt={0}>
                      <Grid item xs={12}>
                        <VuiBox p={2.5} borderRadius="20px" sx={sectionStyles}>
                          <VuiTypography variant="h6" fontWeight="bold" color="dark" mb={2}>
                            Choice Response Breakdown
                          </VuiTypography>

                          <Grid container spacing={2}>
                            {choiceAnalytics.map((item) => (
                              <Grid item xs={12} md={6} key={item.questionId}>
                                <MultipleChoiceAnalyticsCard item={item} />
                              </Grid>
                            ))}
                          </Grid>
                        </VuiBox>
                      </Grid>
                    </Grid>
                  )}

                  {/* Written Feedback */}
                  {textAnalytics.length > 0 && (
                    <Grid container spacing={3} mt={0}>
                      <Grid item xs={12}>
                        <VuiBox p={2.5} borderRadius="20px" sx={sectionStyles}>
                          <VuiTypography variant="h6" fontWeight="bold" color="dark" mb={2}>
                            Written Feedback
                          </VuiTypography>

                          <Grid container spacing={2}>
                            {textAnalytics.map((item) => (
                              <Grid item xs={12} key={item.questionId}>
                                <TextFeedbackAnalyticsCard item={item} />
                              </Grid>
                            ))}
                          </Grid>
                        </VuiBox>
                      </Grid>
                    </Grid>
                  )}

                  {/* No analytics data */}
                  {ratingAnalytics.length === 0 &&
                    choiceAnalytics.length === 0 &&
                    textAnalytics.length === 0 && (
                      <VuiBox
                        minHeight="220px"
                        borderRadius="20px"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        sx={sectionStyles}
                      >
                        <VuiTypography
                          variant="button"
                          color="dark"
                          fontWeight="regular"
                          sx={{ opacity: 0.65 }}
                        >
                          No satisfaction responses were found for the selected date range.
                        </VuiTypography>
                      </VuiBox>
                    )}
                </>
              )}
            </>
          )}
        </Card>
      </VuiBox>
      {formDialogOpen && (
        <GuestSatisfactionFormDialog
          open={formDialogOpen}
          onClose={handleCloseFormDialog}
          form={satisfactionForm}
          onSave={handleSaveForm}
          loading={formActionLoading}
          error={formActionError}
        />
      )}

      <Snackbar
        origin="top"
        color={snackbar.color}
        icon={snackbar.icon}
        title={snackbar.title}
        content={snackbar.content}
        open={snackbar.show}
        onClose={closeSnackbar}
        close={closeSnackbar}
      />
    </DashboardLayout>
  );
}

const sectionStyles = {
  background: rgba(black.main, 0.025),
  border: `${borderWidth[1]} solid ${rgba(black.main, 0.08)}`,
  boxShadow: "none",
};

export default GuestSatisfactionHub;
